import { useEffect, useMemo, useState } from "preact/hooks";
import type { IndexRecord } from "../../shared/snapshot-format";
import type { Character, ItemRunes } from "../character";
import { addCoins, addItem, defaultCarry, itemCandidates, moveToContainer, removeCoins, removeItem, setAmmunition, setCarry, setInSlot, setInvested, setItemHp, setMaterial, setQuantity, setRunes, setTraitToggles } from "../character";
import type { CarryChange, ItemRecordFacts } from "../character";
import { loadCatalogue } from "../data/catalogue";
import { loadEntry } from "../data/entries";
import type { InventoryItemView, InventoryView } from "../engine";
import { ARMOR_PROPERTY_RUNES, REINFORCING_RUNES, RESILIENT_RUNES, STRIKING_RUNES, WEAPON_PROPERTY_RUNES, materialGradesFor, materialsFor, runeLabel } from "../engine";
import { loadEngineCatalogue } from "../data/config";
import type { Catalogue } from "../engine";
import { useSheetActions } from "./sheet-context";

interface Props {
  character: Character;
  view: InventoryView;
  busy: boolean;
  legacy: boolean;
  /** Set when a Session is open: consumable counts then live on the Session, not here. */
  sessionOpen: boolean;
  onSave: (next: Character) => Promise<void>;
  onMessage: (text: string) => void;
}

const TYPE_LABELS: Record<string, string> = { weapon: "Weapon", armor: "Armour", shield: "Shield", equipment: "Gear", consumable: "Consumable", ammo: "Ammunition", backpack: "Container", treasure: "Treasure", book: "Book", kit: "Kit" };
const TYPES = Object.keys(TYPE_LABELS);
const PAGE = 40;

/**
 * The inventory (issue #11): every item with its bulk, price, carry state, container, runes and
 * material; the totals against the Strength thresholds; investiture; coins and wealth; adding
 * from the equipment Pack. Every change is a pure operation from src/character/inventory.ts
 * applied to the Character and saved; the sheet says what is structurally impossible.
 */
export function InventoryPanel({ character, view, busy, legacy, sessionOpen, onSave, onMessage }: Props) {
  const [records, setRecords] = useState<IndexRecord[]>();
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    loadCatalogue().then(setRecords, () => setRecords([]));
    loadEngineCatalogue().then(setCatalogue, () => setCatalogue(null));
  }, []);
  const apply = async (result: { character: Character; blocked: string | null }) => {
    if (result.blocked) {
      onMessage(result.blocked);
      return;
    }
    await onSave(result.character);
  };
  const byId = useMemo(() => new Map(view.items.map((i) => [i.id, i])), [view]);
  const topLevel = view.items.filter((i) => i.container === null);
  const containers = view.items.filter((i) => i.type === "backpack");

  return (
    <section class="inventory">
      <div class="row">
        <h3>
          Inventory <span class="tag">{view.items.length === 1 ? "1 item" : `${view.items.length} items`}</span>
        </h3>
        <span class="actions">
          <button type="button" disabled={busy} onClick={() => setAdding(!adding)}>{adding ? "Close" : "Add an item"}</button>
        </span>
      </div>
      <p class={`muted${view.bulk.encumbered ? " warn" : ""}`}>
        Bulk {view.bulk.label} · encumbered above {view.bulk.encumberedAfter}, limit {view.bulk.max}{view.bulk.encumbered ? " · encumbered" : ""}{view.bulk.overMax ? " · over the limit: cannot move" : ""}
        {" · "}hands free {view.hands.reallyFree} of {view.hands.max}
        {" · "}invested {view.investiture.value} of {view.investiture.max}
      </p>
      <p class="muted">
        Wealth {view.wealth.label} · coins {view.coinsLabel || "none"}{view.wealth.items > 0 ? ` · items worth ${(view.wealth.items / 100).toFixed(2)} gp` : ""}
      </p>
      <CoinsForm character={character} busy={busy} apply={apply} />
      {view.missing.length > 0 && <p class="notice">Entries not found in this Snapshot: {view.missing.map((m) => m.uuid).join(", ")}.</p>}
      {sessionOpen && view.items.some((i) => i.type === "consumable" || i.type === "ammo") && <p class="muted">A Session is open: consumable and ammunition counts at the table are the Session's own; these are what the Character owns.</p>}
      {adding && (
        <div class="card picker">
          <AddItem character={character} records={records ?? []} busy={busy} legacy={legacy} containers={containers} apply={apply} onDone={() => setAdding(false)} />
        </div>
      )}
      {view.items.length === 0 ? (
        <p class="muted">Nothing carried yet. Add weapons, armour and gear from the equipment Pack; a starting kit adds a backpack with the basics.</p>
      ) : (
        <ul class="plain items inventory-items">
          {topLevel.map((item) => <ItemRows key={item.id} item={item} depth={0} byId={byId} character={character} view={view} containers={containers} busy={busy} catalogue={catalogue} editing={editing} setEditing={setEditing} apply={apply} />)}
        </ul>
      )}
    </section>
  );
}

type Apply = (result: { character: Character; blocked: string | null }) => Promise<void>;

function CoinsForm({ character, busy, apply }: { character: Character; busy: boolean; apply: Apply }) {
  const [amount, setAmount] = useState("");
  const [denomination, setDenomination] = useState<"pp" | "gp" | "sp" | "cp">("gp");
  const n = Number(amount);
  const valid = Number.isInteger(n) && n > 0;
  return (
    <p class="inline">
      <input type="number" min="1" placeholder="coins" value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} style="width:6rem" />
      <select value={denomination} onChange={(e) => setDenomination((e.target as HTMLSelectElement).value as "pp" | "gp" | "sp" | "cp")}>
        {["pp", "gp", "sp", "cp"].map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
      <button type="button" disabled={busy || !valid} onClick={() => { void apply(addCoins(character, { [denomination]: n })); setAmount(""); }}>Add</button>
      <button type="button" disabled={busy || !valid} onClick={() => { void apply(removeCoins(character, { [denomination]: n })); setAmount(""); }}>Spend</button>
    </p>
  );
}

function AddItem({ character, records, busy, legacy, containers, apply, onDone }: { character: Character; records: IndexRecord[]; busy: boolean; legacy: boolean; containers: InventoryItemView[]; apply: Apply; onDone: () => void }) {
  const { openEntry } = useSheetActions();
  const [text, setText] = useState("");
  const [type, setType] = useState("");
  const [uncommon, setUncommon] = useState(false);
  const [container, setContainer] = useState("");
  const [quantity, setQuantity] = useState("1");
  const candidates = useMemo(() => itemCandidates(records, { text, ...(type ? { types: [type] } : {}), legacy, uncommon, limit: PAGE }), [records, text, type, legacy, uncommon]);
  const add = async (facts: ItemRecordFacts) => {
    const entry = await loadEntry(facts.uuid);
    if (!entry) return apply({ character, blocked: `${facts.name} could not be loaded` });
    const q = Math.max(1, Math.trunc(Number(quantity)) || 1);
    const holder = containers.find((c) => c.id === container);
    await apply(addItem(character, entry, { quantity: q, container: holder?.id ?? null, ...(holder ? { stows: holder.stowsItems } : {}) }));
    onDone();
  };
  return (
    <div>
      <p class="inline">
        <input type="search" placeholder={records.length > 0 ? "Search the equipment Pack by name" : "Loading…"} value={text} onInput={(e) => setText((e.target as HTMLInputElement).value)} />
        <select value={type} onChange={(e) => setType((e.target as HTMLSelectElement).value)}>
          <option value="">Any type</option>
          {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        <label class="toggle"><input type="checkbox" checked={uncommon} onChange={(e) => setUncommon((e.target as HTMLInputElement).checked)} /> uncommon and rare</label>
        <input type="number" min="1" value={quantity} onInput={(e) => setQuantity((e.target as HTMLInputElement).value)} style="width:5rem" title="Quantity" />
        {containers.length > 0 && (
          <select value={container} onChange={(e) => setContainer((e.target as HTMLSelectElement).value)}>
            <option value="">Not in a container</option>
            {containers.map((c) => <option key={c.id} value={c.id}>In {c.name}</option>)}
          </select>
        )}
      </p>
      {text.trim() === "" && !type ? <p class="muted">Type a name, or pick a type to browse.</p> : candidates.length === 0 ? <p class="muted">Nothing matches{legacy ? "" : " among Remaster Entries (show Legacy to widen)"}.</p> : null}
      <ul class="candidates">
        {candidates.map((c) => (
          <li key={c.uuid}>
            <button type="button" class="icon info" aria-label={`Read ${c.name}`} onClick={() => openEntry(c.uuid)}>i</button>
            <button type="button" class="pick" disabled={busy} onClick={() => void add(c)}>
              <span>{c.name}</span>
              <span class="pick-tags">
                <span class="tag">{TYPE_LABELS[c.type] ?? c.type}</span>
                {c.level > 0 ? <span class="tag">L{c.level}</span> : null}
                {c.rarity !== "common" ? <span class="tag">{c.rarity}</span> : null}
                {!c.remaster ? <span class="tag legacy">Legacy</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RowProps {
  item: InventoryItemView;
  depth: number;
  byId: Map<string, InventoryItemView>;
  character: Character;
  view: InventoryView;
  containers: InventoryItemView[];
  busy: boolean;
  catalogue: Catalogue | null;
  editing: string | null;
  setEditing: (id: string | null) => void;
  apply: Apply;
}

function carryValue(item: InventoryItemView): string {
  if (item.carryType === "held") return `held:${item.handsHeld}`;
  if (item.carryType === "worn" && item.inSlot === true) return "slot";
  return item.carryType;
}

function ItemRows(props: RowProps) {
  const { item, depth, byId, character, view, containers, busy, catalogue, editing, setEditing, apply } = props;
  const { openEntry } = useSheetActions();
  const open = editing === item.id;
  const stateTags: string[] = [];
  if (item.equipped && item.carryType === "held") stateTags.push(`held (${item.handsHeld}h)`);
  else if (item.equipped && item.usage.type === "worn") stateTags.push(item.usage.where ? "worn" : "on person");
  else if (item.carryType === "dropped") stateTags.push("dropped");
  else if (item.stowed) stateTags.push("stowed");
  else if (!item.equipped && item.usage.type !== "carried") stateTags.push("carried");
  if (item.invested === true) stateTags.push("invested");
  if (item.invested === false && item.equipped) stateTags.push("not invested");
  if (item.broken) stateTags.push("broken");
  if (item.destroyed) stateTags.push("destroyed");
  const onCarry = (value: string) => {
    if (value === "slot") return void apply(setInSlot(character, view, item.id, true));
    const change: CarryChange = value.startsWith("held:") ? { carryType: "held", handsHeld: Number(value.slice(5)) as 1 | 2 } : { carryType: value as CarryChange["carryType"] };
    if (item.usage.type === "worn" && item.usage.where && item.inSlot === true && change.carryType === "worn") return void apply(setInSlot(character, view, item.id, false));
    void apply(setCarry(character, view, item.id, change));
  };
  const slotted = item.usage.type === "worn" && !!item.usage.where;
  return (
    <>
      <li class={`inventory-item depth-${Math.min(depth, 3)}${item.active || item.rules === 0 ? "" : " inactive"}`}>
        <span class="item-main">
          <button type="button" class="icon info" aria-label={`Read ${item.name}`} onClick={() => openEntry(item.uuid)}>i</button>
          <button type="button" class="linkish" onClick={() => setEditing(open ? null : item.id)} title="Runes, material and more">{item.name}</button>
          <span class="tag">{TYPE_LABELS[item.type] ?? item.type}</span>
          {item.level > 0 ? <span class="tag">L{item.level}</span> : null}
          {stateTags.map((t) => <span key={t} class={`tag${t === "broken" || t === "destroyed" ? " warn" : ""}`}>{t}</span>)}
          {item.rules > 0 ? <span class="tag" title={item.active ? "Its Rule Elements apply" : "Its Rule Elements wait: equip (and invest) it"}>{item.active ? "active" : "off"}</span> : null}
          <span class="muted"> · {item.bulk.totalLabel !== item.bulk.unitLabel && item.type === "backpack" ? `${item.bulk.totalLabel} bulk with contents` : `${item.bulk.unitLabel} bulk`} · {item.price.unitLabel}{item.quantity > 1 ? ` each` : ""}</span>
          {item.problems.map((p, i) => <span key={i} class="tag warn">{p}</span>)}
        </span>
        <span class="actions">
          <span class="pips">
            <button type="button" disabled={busy || item.quantity <= 0} onClick={() => void apply(setQuantity(character, item.id, item.quantity - 1))}>−</button>
            <span>×{item.quantity}</span>
            <button type="button" disabled={busy} onClick={() => void apply(setQuantity(character, item.id, item.quantity + 1))}>+</button>
          </span>
          <select value={carryValue(item)} disabled={busy} onChange={(e) => onCarry((e.target as HTMLSelectElement).value)} title="How it is carried">
            {item.usage.type === "held" || item.type === "weapon" || item.type === "shield" ? (
              <>
                <option value="held:1">Held, one hand</option>
                <option value="held:2">Held, two hands</option>
              </>
            ) : null}
            {slotted ? <option value="slot">Worn ({item.usage.where})</option> : null}
            <option value="worn">{slotted ? "Carried, not worn" : item.usage.type === "worn" ? "Worn" : "Carried"}</option>
            <option value="stowed">Stowed</option>
            <option value="dropped">Dropped</option>
          </select>
          {item.invested !== null && (
            <label class="toggle"><input type="checkbox" checked={item.invested} disabled={busy} onChange={(e) => void apply(setInvested(character, view, item.id, (e.target as HTMLInputElement).checked))} /> invest</label>
          )}
          {containers.length > 0 && item.type !== "backpack" ? (
            <select value={item.container ?? ""} disabled={busy} onChange={(e) => void apply(moveToContainer(character, view, item.id, (e.target as HTMLSelectElement).value || null))} title="Container">
              <option value="">No container</option>
              {containers.filter((c) => c.id !== item.id).map((c) => <option key={c.id} value={c.id}>In {c.name}</option>)}
            </select>
          ) : null}
          <button type="button" disabled={busy} onClick={() => void apply(removeItem(character, item.id))}>Remove</button>
        </span>
        {open && <ItemDetails item={item} character={character} view={view} busy={busy} catalogue={catalogue} apply={apply} />}
      </li>
      {item.contents.map((id) => {
        const child = byId.get(id);
        return child ? <ItemRows key={id} {...props} item={child} depth={depth + 1} /> : null;
      })}
    </>
  );
}

function ItemDetails({ item, character, view, busy, catalogue, apply }: { item: InventoryItemView; character: Character; view: InventoryView; busy: boolean; catalogue: Catalogue | null; apply: Apply }) {
  const runeTable = item.type === "armor" ? ARMOR_PROPERTY_RUNES : WEAPON_PROPERTY_RUNES;
  const takesRunes = item.type === "weapon" || item.type === "armor" || item.type === "shield";
  const materials = materialsFor({ type: item.type, baseType: null });
  const [runeToAdd, setRuneToAdd] = useState("");
  const [material, setMaterialType] = useState(item.material.type ?? "");
  const grades = material ? materialGradesFor({ type: item.type, baseType: null }, material) : [];
  const setRune = (change: Partial<ItemRunes>) => {
    const runes: ItemRunes = { ...item.runes, ...change };
    void apply(setRunes(character, view, item.id, runes));
  };
  const propertyOptions = Object.keys(runeTable).sort((a, b) => runeLabel(catalogue, item.type === "armor" ? "armor" : "weapon", a).localeCompare(runeLabel(catalogue, item.type === "armor" ? "armor" : "weapon", b)));
  return (
    <div class="item-details">
      <p class="muted">
        {item.usageLabel} · {item.rarity !== "common" ? `${item.rarity} · ` : ""}{item.traits.join(", ") || "no traits"}
        {item.armor ? ` · AC +${item.armor.acBonus}, Dex cap +${item.armor.dexCap}, check ${item.armor.checkPenalty}, speed ${item.armor.speedPenalty}${item.armor.strength !== null ? `, Str +${item.armor.strength}` : ""}` : ""}
        {item.shield ? ` · shield AC +${item.shield.acBonus}${item.shield.raised ? " (raised)" : ""}, hardness ${item.hardness}` : ""}
        {item.hp ? ` · HP ${item.hp.value} / ${item.hp.max} (BT ${item.hp.brokenThreshold})` : ""}
        {item.capacity ? ` · holds ${item.capacity.value} of ${item.capacity.max} bulk, ignores ${item.capacity.ignored}` : ""}
        {item.apex ? ` · apex (${item.apex})` : ""}
        {item.runeLabels.length > 0 ? ` · ${item.runeLabels.join(", ")}` : ""}
        {item.material.label ? ` · ${item.material.label}` : ""}
      </p>
      {item.hp && (
        <p class="inline">
          <span class="muted">Hit Points:</span>
          <button type="button" disabled={busy || item.hp.value <= 0} onClick={() => void apply(setItemHp(character, view, item.id, item.hp!.value - 1))}>−</button>
          <span>{item.hp.value} / {item.hp.max}</span>
          <button type="button" disabled={busy || item.hp.value >= item.hp.max} onClick={() => void apply(setItemHp(character, view, item.id, item.hp!.value + 1))}>+</button>
          {item.hp.value < item.hp.max ? <button type="button" disabled={busy} onClick={() => void apply(setItemHp(character, view, item.id, item.hp!.max))}>Repair</button> : null}
        </p>
      )}
      {item.ammunition && (
        <p class="inline">
          <span class="muted">Ammunition:</span>
          <select value={item.ammunition.selected ?? ""} disabled={busy} onChange={(e) => void apply(setAmmunition(character, view, item.id, (e.target as HTMLSelectElement).value || null))}>
            <option value="">{item.ammunition.compatible.length === 0 ? `None carried${item.ammunition.baseType ? ` (${item.ammunition.baseType})` : ""}` : "None selected"}</option>
            {item.ammunition.compatible.map((a) => <option key={a.id} value={a.id}>{a.name} ×{a.quantity}</option>)}
          </select>
        </p>
      )}
      {item.type === "weapon" && item.traits.some((t) => t.startsWith("versatile-") || t === "modular") && (
        <p class="inline">
          <span class="muted">Damage type:</span>
          <button type="button" disabled={busy} onClick={() => void apply(setTraitToggles(character, item.id, { versatile: null }))}>Base</button>
          {item.traits.filter((t) => t.startsWith("versatile-")).map((t) => {
            const short = t.slice("versatile-".length);
            const type = short === "b" ? "bludgeoning" : short === "p" ? "piercing" : short === "s" ? "slashing" : short;
            return <button type="button" key={t} disabled={busy} onClick={() => void apply(setTraitToggles(character, item.id, { versatile: type }))}>{type}</button>;
          })}
        </p>
      )}
      {takesRunes && !item.specific && (
        <div class="runes-form">
          <p class="inline">
            {item.type !== "shield" && (
              <label>Potency
                <select value={item.runes.potency} disabled={busy} onChange={(e) => setRune({ potency: Number((e.target as HTMLSelectElement).value) })}>
                  {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n === 0 ? "none" : `+${n}`}</option>)}
                </select>
              </label>
            )}
            {item.type === "weapon" && (
              <label>Striking
                <select value={item.runes.striking} disabled={busy} onChange={(e) => setRune({ striking: Number((e.target as HTMLSelectElement).value) })}>
                  <option value={0}>none</option>
                  {([1, 2, 3, 4] as const).map((n) => <option key={n} value={n}>{catalogue?.localize(STRIKING_RUNES[n].name) ?? STRIKING_RUNES[n].slug}</option>)}
                </select>
              </label>
            )}
            {item.type === "armor" && (
              <label>Resilient
                <select value={item.runes.resilient} disabled={busy} onChange={(e) => setRune({ resilient: Number((e.target as HTMLSelectElement).value) })}>
                  <option value={0}>none</option>
                  {([1, 2, 3, 4] as const).map((n) => <option key={n} value={n}>{catalogue?.localize(RESILIENT_RUNES[n].name) ?? RESILIENT_RUNES[n].slug}</option>)}
                </select>
              </label>
            )}
            {item.type === "shield" && (
              <label>Reinforcing
                <select value={item.runes.reinforcing} disabled={busy} onChange={(e) => setRune({ reinforcing: Number((e.target as HTMLSelectElement).value) })}>
                  <option value={0}>none</option>
                  {([1, 2, 3, 4, 5, 6] as const).map((n) => <option key={n} value={n}>{catalogue?.localize(REINFORCING_RUNES[n].name) ?? n}</option>)}
                </select>
              </label>
            )}
          </p>
          {item.type !== "shield" && (
            <p class="inline">
              <span class="muted">Property runes ({item.runes.property.length} of {item.runeSlots} slots):</span>
              {item.runes.property.map((p) => (
                <span key={p} class="tag">
                  {runeLabel(catalogue, item.type === "armor" ? "armor" : "weapon", p)}{" "}
                  <button type="button" class="linkish" disabled={busy} onClick={() => setRune({ property: item.runes.property.filter((r) => r !== p) })}>×</button>
                </span>
              ))}
              <select value={runeToAdd} disabled={busy || item.runes.property.length >= item.runeSlots} onChange={(e) => setRuneToAdd((e.target as HTMLSelectElement).value)}>
                <option value="">{item.runeSlots === 0 ? "No slot (needs a potency rune)" : "Add a rune…"}</option>
                {propertyOptions.filter((p) => !item.runes.property.includes(p)).map((p) => <option key={p} value={p}>{runeLabel(catalogue, item.type === "armor" ? "armor" : "weapon", p)} (L{runeTable[p]!.level}, {runeTable[p]!.price} gp)</option>)}
              </select>
              <button type="button" disabled={busy || !runeToAdd} onClick={() => { setRune({ property: [...item.runes.property, runeToAdd] }); setRuneToAdd(""); }}>Etch</button>
            </p>
          )}
        </div>
      )}
      {materials.length > 0 && !item.specific && (
        <p class="inline">
          <span class="muted">Material:</span>
          <select value={material} disabled={busy} onChange={(e) => { const value = (e.target as HTMLSelectElement).value; setMaterialType(value); if (!value) void apply(setMaterial(character, view, item.id, null)); }}>
            <option value="">Ordinary</option>
            {materials.map((m) => <option key={m} value={m}>{catalogue?.table("preciousMaterials")?.[m] ?? m}</option>)}
          </select>
          {material && (
            <select value={item.material.type === material ? (item.material.grade ?? "") : ""} disabled={busy} onChange={(e) => { const grade = (e.target as HTMLSelectElement).value as "low" | "standard" | "high" | ""; if (grade) void apply(setMaterial(character, view, item.id, { type: material, grade })); }}>
              <option value="">Grade…</option>
              {grades.map((g) => <option key={g} value={g}>{catalogue?.table("preciousMaterialGrades")?.[g] ?? g}</option>)}
            </select>
          )}
        </p>
      )}
      {item.specific && <p class="muted">A specific magic item: its runes and material are its own.</p>}
    </div>
  );
}

export { defaultCarry };
